import React, { useState } from 'react';
import { 
  Users, Shield, UserPlus, Settings, Search, Filter, 
  AlertTriangle, CheckCircle, XCircle, Eye, Edit,
  Clock, FileText, Lock, Database, Activity
} from 'lucide-react';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import Select from '../../components/atoms/Select';
import StatCard from '../../components/molecules/StatCard';
import DashboardLayout from '../../components/layout/DashboardLayout';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

interface Role {
  id: string;
  name: string;
  description: string;
  color: string;
  permissions: string[];
}

interface Employee {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  region: string;
  status: 'active' | 'suspended' | 'inactive';
  createdAt: string;
}

interface AuditLog {
  id: string;
  user: string;
  action: string;
  details: string;
  timestamp: string;
  approver?: string;
}

const UserManagementDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'roles' | 'employees' | 'permissions' | 'audit'>('roles');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [showAuditDetailsModal, setShowAuditDetailsModal] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(null);
  
  // Employee Management State
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [showEditEmployeeModal, setShowEditEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [employeesList, setEmployeesList] = useState<Employee[]>([]);
  const [customRoles, setCustomRoles] = useState<Role[]>([]);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRoleForView, setSelectedRoleForView] = useState<Role | null>(null);
  const [showApprovalWorkflowModal, setShowApprovalWorkflowModal] = useState(false);
  const [showPermissionConfigModal, setShowPermissionConfigModal] = useState(false);
  const [selectedPermissionForConfig, setSelectedPermissionForConfig] = useState<any>(null);

  // System default roles with HIRSTO colors
  const systemRoles: Role[] = [
    {
      id: 'admin',
      name: 'Administrator',
      description: 'Complete access to all system features and settings',
      color: '#03045E', // Dark Blue
      permissions: ['all']
    },
    {
      id: 'automation-manager',
      name: 'Automation Manager',
      description: 'Manages automation rules and operational efficiency',
      color: '#0077B6', // Bright Blue
      permissions: ['modify_stp_rules', 'view_efficiency', 'redirect_tasks']
    },
    {
      id: 'fwa-auditor',
      name: 'FWA Fraud Auditor',
      description: 'Fraud detection and investigation specialist',
      color: '#235DFF', // Electric Blue
      permissions: ['freeze_payments', 'start_investigation', 'modify_alerts']
    },
    {
      id: 'actuarial-analyst',
      name: 'Actuarial Analyst',
      description: 'Risk analysis and dynamic pricing management',
      color: '#5C7A99', // Steel Blue
      permissions: ['modify_pricing', 'export_data', 'view_analytics']
    },
    {
      id: 'claims-processor',
      name: 'Claims Processor',
      description: 'Limited access for manual claim review tasks',
      color: '#0096C7', // Ocean Blue
      permissions: ['process_claims', 'view_alerts']
    }
  ];

  // Mock employees data
  const [employees] = useState<Employee[]>([
    {
      id: '1',
      name: 'Maram Al-Sayed',
      email: 'maram.s@company.ae',
      role: 'FWA Fraud Auditor',
      department: 'FWA',
      region: 'All Regions',
      status: 'active',
      createdAt: '2025-01-15'
    },
    {
      id: '2',
      name: 'Ahmed Al-Mansouri',
      email: 'ahmed.m@company.ae',
      role: 'Automation Manager',
      department: 'Claims',
      region: 'Dubai',
      status: 'active',
      createdAt: '2025-02-01'
    }
  ]);

  // Mock audit logs
  const auditLogs: AuditLog[] = [
    {
      id: '1',
      user: 'Maram Al-Sayed',
      action: 'Froze Provider Payment',
      details: 'Froze payments to Dr. Smith Clinic - Reason: Suspected fraudulent claims',
      timestamp: '2025-10-01 14:30:00',
      approver: 'Admin'
    },
    {
      id: '2',
      user: 'Ahmed Al-Mansouri',
      action: 'Modified STP Rule',
      details: 'Changed auto-approval limit from AED 5,000 to AED 7,000',
      timestamp: '2025-10-01 10:15:00',
      approver: 'Risk Manager'
    },
    {
      id: '3',
      user: 'Maram Al-Sayed',
      action: 'Started Investigation',
      details: 'Launched fraud investigation for Dr. Omar Al-Kaabi - Case #1023',
      timestamp: '2025-10-02 09:45:00',
      approver: 'FWA Manager'
    },
    {
      id: '4',
      user: 'Ahmed Al-Mansouri',
      action: 'Redirected Claim',
      details: 'Redirected claim #CLM-2025-5432 from automated queue to manual review',
      timestamp: '2025-10-02 16:20:00'
    },
    {
      id: '5',
      user: 'Admin',
      action: 'Created Employee Account',
      details: 'Created new account for Sara Al-Mansouri - Role: Claims Processor',
      timestamp: '2025-10-03 11:00:00'
    },
    {
      id: '6',
      user: 'Maram Al-Sayed',
      action: 'Exported Provider Report',
      details: 'Exported fraud analysis report for Dubai Medical Center',
      timestamp: '2025-10-03 14:15:00'
    },
    {
      id: '7',
      user: 'Ahmed Al-Mansouri',
      action: 'Modified Automation Rule',
      details: 'Updated STP rule for pharmacy claims - New threshold: AED 3,000',
      timestamp: '2025-10-04 08:30:00',
      approver: 'Risk Manager'
    },
    {
      id: '8',
      user: 'Admin',
      action: 'Suspended Employee Account',
      details: 'Suspended account for Khalid Hassan - Reason: Security review',
      timestamp: '2025-10-04 10:00:00'
    }
  ];

  const handleViewAuditDetails = (log: AuditLog) => {
    setSelectedAuditLog(log);
    setShowAuditDetailsModal(true);
  };

  const handleExportAuditPDF = () => {
    const filteredLogs = getFilteredAuditLogs();
    
    if (filteredLogs.length === 0) {
      toast.error('No audit log entries to export');
      return;
    }

    toast.loading(`Generating PDF with ${filteredLogs.length} audit log entries...`);
    
    // Generate PDF content
    const pdfContent = generateAuditLogPDF(filteredLogs);
    
    // Create blob and download
    const blob = new Blob([pdfContent], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Audit_Log_Report_${dayjs().format('YYYY-MM-DD_HHmmss')}.pdf`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.dismiss();
    toast.success(`Audit log PDF downloaded successfully (${filteredLogs.length} entries)`);
  };

  const handleExportAuditCSV = () => {
    const filteredLogs = getFilteredAuditLogs();
    
    if (filteredLogs.length === 0) {
      toast.error('No audit log entries to export');
      return;
    }

    toast.loading(`Generating CSV with ${filteredLogs.length} audit log entries...`);
    
    // Generate CSV content
    const csvContent = generateAuditLogCSV(filteredLogs);
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Audit_Log_Export_${dayjs().format('YYYY-MM-DD_HHmmss')}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.dismiss();
    toast.success(`Audit log CSV downloaded successfully (${filteredLogs.length} entries)`);
  };

  const handleGenerateReport = () => {
    const filteredLogs = getFilteredAuditLogs();
    
    if (filteredLogs.length === 0) {
      toast.error('No audit log entries to generate report');
      return;
    }

    toast.loading(`Generating comprehensive audit report...`);
    
    // Generate comprehensive report PDF
    const reportContent = generateComprehensiveAuditReport(filteredLogs);
    
    // Create blob and download
    const blob = new Blob([reportContent], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Comprehensive_Audit_Report_${dayjs().format('YYYY-MM-DD_HHmmss')}.pdf`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.dismiss();
    toast.success(`Comprehensive audit report generated successfully (${filteredLogs.length} entries)`);
  };

  const getFilteredAuditLogs = () => {
    let filtered = auditLogs;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by user
    if (selectedUser) {
      filtered = filtered.filter(log => log.user === selectedUser);
    }

    // Filter by date range
    if (fromDate) {
      filtered = filtered.filter(log => dayjs(log.timestamp).isAfter(dayjs(fromDate).startOf('day')));
    }

    if (toDate) {
      filtered = filtered.filter(log => dayjs(log.timestamp).isBefore(dayjs(toDate).endOf('day')));
    }

    return filtered;
  };

  // Generate PDF content for audit logs
  const generateAuditLogPDF = (logs: typeof auditLogs): string => {
    const currentDate = dayjs().format('MMMM D, YYYY HH:mm:ss');
    
    let logsContent = '';
    let yPosition = 650;
    
    logs.forEach((log, index) => {
      logsContent += `
0 -30 Td
/F1 10 Tf
(${index + 1}. ${log.action}) Tj
0 -15 Td
(User: ${log.user} | Time: ${dayjs(log.timestamp).format('MMM D, YYYY HH:mm')}) Tj
0 -15 Td
(Details: ${log.details.substring(0, 80)}${log.details.length > 80 ? '...' : ''}) Tj
0 -15 Td
(Approver: ${log.approver || 'N/A'}) Tj`;
      yPosition -= 75;
    });

    return `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj
2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj
3 0 obj
<<
/Type /Page
/Parent 2 0 R
/Resources <<
/Font <<
/F1 4 0 R
>>
>>
/MediaBox [0 0 612 792]
/Contents 5 0 R
>>
endobj
4 0 obj
<<
/Type /Font
/Subtype /Type1
/BaseFont /Helvetica
>>
endobj
5 0 obj
<<
/Length 2000
>>
stream
BT
/F1 18 Tf
50 750 Td
(HIRSTO Audit Log Report) Tj
0 -30 Td
/F1 10 Tf
(Generated: ${currentDate}) Tj
0 -15 Td
(Total Entries: ${logs.length}) Tj
0 -15 Td
(Report ID: ${Math.random().toString(36).substr(2, 9).toUpperCase()}) Tj
0 -40 Td
/F1 12 Tf
(Audit Log Entries:) Tj
${logsContent}
ET
endstream
endobj
xref
0 6
0000000000 65535 f
0000000009 00000 n
0000000058 00000 n
0000000115 00000 n
0000000262 00000 n
0000000339 00000 n
trailer
<<
/Size 6
/Root 1 0 R
>>
startxref
2500
%%EOF`;
  };

  // Generate CSV content for audit logs
  const generateAuditLogCSV = (logs: typeof auditLogs): string => {
    const headers = [
      'Timestamp',
      'Action',
      'User',
      'Details',
      'Approved By'
    ];
    
    const rows = logs.map(log => [
      dayjs(log.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      `"${log.action.replace(/"/g, '""')}"`,
      log.user,
      `"${log.details.replace(/"/g, '""')}"`, // Escape quotes in CSV
      log.approver || 'N/A'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    return csvContent;
  };

  // Generate comprehensive audit report PDF
  const generateComprehensiveAuditReport = (logs: typeof auditLogs): string => {
    const currentDate = dayjs().format('MMMM D, YYYY HH:mm:ss');
    
    // Calculate statistics
    const totalActions = logs.length;
    const approvedActions = logs.filter(log => log.approver).length;
    const uniqueUsers = [...new Set(logs.map(log => log.user))].length;
    const dateRange = logs.length > 0 ? 
      `${dayjs(logs[0].timestamp).format('MMM D, YYYY')} - ${dayjs(logs[logs.length - 1].timestamp).format('MMM D, YYYY')}` : 
      'N/A';
    
    let logsContent = '';
    logs.forEach((log, index) => {
      logsContent += `
0 -25 Td
/F1 10 Tf
(${index + 1}. ${log.action}) Tj
0 -12 Td
(User: ${log.user} | ${dayjs(log.timestamp).format('MMM D, YYYY HH:mm')}) Tj
0 -12 Td
(${log.details.substring(0, 70)}${log.details.length > 70 ? '...' : ''}) Tj`;
    });

    return `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj
2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj
3 0 obj
<<
/Type /Page
/Parent 2 0 R
/Resources <<
/Font <<
/F1 4 0 R
>>
>>
/MediaBox [0 0 612 792]
/Contents 5 0 R
>>
endobj
4 0 obj
<<
/Type /Font
/Subtype /Type1
/BaseFont /Helvetica-Bold
>>
endobj
5 0 obj
<<
/Length 2500
>>
stream
BT
/F1 20 Tf
50 750 Td
(HIRSTO Comprehensive Audit Report) Tj
0 -35 Td
/F1 10 Tf
(Generated: ${currentDate}) Tj
0 -15 Td
(Report ID: ${Math.random().toString(36).substr(2, 9).toUpperCase()}) Tj
0 -40 Td
/F1 14 Tf
(Executive Summary) Tj
0 -25 Td
/F1 10 Tf
(Total Audit Entries: ${totalActions}) Tj
0 -15 Td
(Actions with Approval: ${approvedActions}) Tj
0 -15 Td
(Actions without Approval: ${totalActions - approvedActions}) Tj
0 -15 Td
(Unique Users: ${uniqueUsers}) Tj
0 -15 Td
(Date Range: ${dateRange}) Tj
0 -40 Td
/F1 14 Tf
(Detailed Audit Log) Tj
${logsContent}
0 -40 Td
/F1 10 Tf
(--- End of Report ---) Tj
0 -15 Td
(This report complies with HIRSTO audit requirements:) Tj
0 -12 Td
(1. Who performed the action) Tj
0 -12 Td
(2. What exactly was the action) Tj
0 -12 Td
(3. When the action occurred) Tj
0 -12 Td
(4. Who approved it (if dual approval required)) Tj
ET
endstream
endobj
xref
0 6
0000000000 65535 f
0000000009 00000 n
0000000058 00000 n
0000000115 00000 n
0000000262 00000 n
0000000344 00000 n
trailer
<<
/Size 6
/Root 1 0 R
>>
startxref
3000
%%EOF`;
  };

  // Load employees from localStorage on mount
  React.useEffect(() => {
    const storedEmployees = localStorage.getItem('hirsto_employees_list');
    if (storedEmployees) {
      try {
        setEmployeesList(JSON.parse(storedEmployees));
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    } else {
      // Set initial employees
      setEmployeesList(employees);
    }

    const storedCustomRoles = localStorage.getItem('hirsto_custom_roles');
    if (storedCustomRoles) {
      try {
        setCustomRoles(JSON.parse(storedCustomRoles));
      } catch (error) {
        console.error('Error loading custom roles:', error);
      }
    } else {
      // Initialize with demo custom roles
      const demoCustomRoles: Role[] = [
        {
          id: 'custom-network',
          name: 'Network and Pharmacy Manager',
          description: 'Manages network providers and pharmacy relationships',
          color: '#8338EC', // Purple
          permissions: ['view_fraud_detection', 'view_analytics', 'freeze_payments']
        },
        {
          id: 'custom-compliance',
          name: 'Compliance Officer',
          description: 'Ensures regulatory compliance and audit oversight',
          color: '#FB5607', // Orange
          permissions: ['view_analytics', 'export_data', 'view_fraud_detection']
        }
      ];
      setCustomRoles(demoCustomRoles);
      localStorage.setItem('hirsto_custom_roles', JSON.stringify(demoCustomRoles));
    }
  }, []);

  // Save employees to localStorage whenever they change
  React.useEffect(() => {
    if (employeesList.length > 0) {
      localStorage.setItem('hirsto_employees_list', JSON.stringify(employeesList));
    }
  }, [employeesList]);

  // Save custom roles to localStorage whenever they change
  React.useEffect(() => {
    localStorage.setItem('hirsto_custom_roles', JSON.stringify(customRoles));
  }, [customRoles]);

  // Employee Management Functions
  const handleAddEmployee = (newEmployee: Omit<Employee, 'id' | 'createdAt'> & { password: string }) => {
    const employee: Employee = {
      ...newEmployee,
      id: `emp-${Date.now()}`,
      createdAt: dayjs().format('YYYY-MM-DD'),
      status: 'active'
    };

    // Add to employees list
    setEmployeesList([...employeesList, employee]);

    // Store credentials in localStorage for login
    const storedEmployees = localStorage.getItem('hirsto_employees');
    const employeesAuth = storedEmployees ? JSON.parse(storedEmployees) : {};
    
    employeesAuth[newEmployee.email.toLowerCase()] = {
      password: newEmployee.password,
      user: {
        id: employee.id,
        name: newEmployee.name,
        email: newEmployee.email,
        role: newEmployee.role,
        department: newEmployee.department,
        region: newEmployee.region,
        permissions: getRolePermissions(newEmployee.role)
      }
    };
    
    localStorage.setItem('hirsto_employees', JSON.stringify(employeesAuth));

    toast.success(`Employee ${newEmployee.name} added successfully`);
    setShowAddEmployeeModal(false);
  };

  const handleEditEmployee = (updatedEmployee: Employee) => {
    setEmployeesList(employeesList.map(emp => 
      emp.id === updatedEmployee.id ? updatedEmployee : emp
    ));
    
    // Update auth info if exists
    const storedEmployees = localStorage.getItem('hirsto_employees');
    if (storedEmployees) {
      const employeesAuth = JSON.parse(storedEmployees);
      if (employeesAuth[updatedEmployee.email.toLowerCase()]) {
        employeesAuth[updatedEmployee.email.toLowerCase()].user = {
          ...employeesAuth[updatedEmployee.email.toLowerCase()].user,
          name: updatedEmployee.name,
          role: updatedEmployee.role,
          department: updatedEmployee.department,
          region: updatedEmployee.region,
          permissions: getRolePermissions(updatedEmployee.role)
        };
        localStorage.setItem('hirsto_employees', JSON.stringify(employeesAuth));
      }
    }

    toast.success(`Employee ${updatedEmployee.name} updated successfully`);
    setShowEditEmployeeModal(false);
    setSelectedEmployee(null);
  };

  const handleRevokeEmployee = (employee: Employee) => {
    if (window.confirm(`⚠️ EMERGENCY REVOCATION\n\nAre you sure you want to immediately disable access for ${employee.name}?\n\nThis action will:\n- Immediately terminate all active sessions\n- Lock the account\n- Log this action in the audit trail\n\nThis cannot be undone without administrator approval.`)) {
      setEmployeesList(employeesList.map(emp =>
        emp.id === employee.id ? { ...emp, status: 'suspended' as const } : emp
      ));
      toast.error(`Access revoked for ${employee.name}`);
    }
  };

  const handleCreateCustomRole = (newRole: Omit<Role, 'id'>) => {
    const role: Role = {
      ...newRole,
      id: `custom-${Date.now()}`
    };
    setCustomRoles([...customRoles, role]);
    toast.success(`Custom role "${newRole.name}" created successfully`);
    setShowCreateRoleModal(false);
  };

  const getRolePermissions = (roleName: string): string[] => {
    // Check system roles first
    const systemRole = systemRoles.find(r => r.name === roleName);
    if (systemRole) return systemRole.permissions;
    
    // Check custom roles
    const customRole = customRoles.find(r => r.name === roleName);
    if (customRole) return customRole.permissions;
    
    return [];
  };

  const permissionsMatrix = [
    {
      screen: 'Registration & Integration',
      automationManager: { level: 'View', color: 'blue' },
      fwaAuditor: { level: 'Denied', color: 'red' },
      claimsProcessor: { level: 'Denied', color: 'red' },
      actuarialAnalyst: { level: 'Denied', color: 'red' }
    },
    {
      screen: 'Operational Efficiency',
      automationManager: { level: 'Modify', color: 'green' },
      fwaAuditor: { level: 'View', color: 'blue' },
      claimsProcessor: { level: 'Interact', color: 'yellow' },
      actuarialAnalyst: { level: 'View', color: 'blue' }
    },
    {
      screen: 'Fraud Detection Module',
      automationManager: { level: 'View', color: 'blue' },
      fwaAuditor: { level: 'Full Access', color: 'green' },
      claimsProcessor: { level: 'View', color: 'blue' },
      actuarialAnalyst: { level: 'View', color: 'blue' }
    },
    {
      screen: 'Proactive Risk Screen',
      automationManager: { level: 'Denied', color: 'red' },
      fwaAuditor: { level: 'View', color: 'blue' },
      claimsProcessor: { level: 'Denied', color: 'red' },
      actuarialAnalyst: { level: 'Full Access', color: 'green' }
    }
  ];

  const criticalPermissions = [
    {
      permission: 'Change Automation Rules (STP)',
      purpose: 'Prevent reducing audit filters that may increase fraud',
      authorizedRole: 'Automation Manager',
      requiresApproval: true
    },
    {
      permission: 'Provider Payment Freeze',
      purpose: 'Stop money flow to suspected providers immediately',
      authorizedRole: 'FWA Auditor',
      requiresApproval: false
    },
    {
      permission: 'Access Individual Vital Sign Data',
      purpose: 'Ensure customer privacy compliance (GDPR/HIPAA)',
      authorizedRole: 'Reviewing Physicians Only',
      requiresApproval: true
    },
    {
      permission: 'Raw Data Export',
      purpose: 'Prevent massive data leak from Data Lake',
      authorizedRole: 'Admin & Actuarial Analyst',
      requiresApproval: true
    },
    {
      permission: 'Activate/Deactivate Employee Accounts',
      purpose: 'Control access for new and departing employees',
      authorizedRole: 'System Administrator',
      requiresApproval: false
    }
  ];



  const renderRolesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Roles</h2>
          <p className="text-gray-600 mt-1">Default roles with pre-configured permissions</p>
        </div>
        <Button icon={UserPlus} onClick={() => setShowCreateRoleModal(true)}>
          Create Custom Role
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...systemRoles, ...customRoles].map((role) => (
          <div key={role.id} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${role.color}15` }}
            >
              <Shield className="w-6 h-6" style={{ color: role.color }} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{role.name}</h3>
            <p className="text-sm text-gray-600 mb-4">{role.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">{role.permissions.length} permissions</span>
              <Button 
                variant="outline" 
                size="sm" 
                icon={Eye}
                onClick={() => {
                  setSelectedRoleForView(role);
                  setShowPermissionsModal(true);
                }}
              >
                View
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Custom Roles Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200 p-8 text-center">
        <div className="w-16 h-16 bg-[#235DFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Settings className="w-8 h-8 text-[#235DFF]" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Create Custom Roles</h3>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          Design specialized roles with unique permission combinations to match your organizational structure
          (e.g., "Network and Pharmacy Manager")
        </p>
        <Button icon={UserPlus} size="lg" onClick={() => setShowCreateRoleModal(true)}>
          Create New Role
        </Button>
      </div>
    </div>
  );

  const renderEmployeesTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Employee Management</h2>
          <p className="text-gray-600 mt-1">Add and manage employee accounts</p>
        </div>
        <Button icon={UserPlus} onClick={() => setShowAddEmployeeModal(true)}>
          Add New Employee
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Employees"
          value={employeesList.length}
          icon={Users}
          iconBgColor="bg-[#0077B6]/10"
          iconColor="text-[#0077B6]"
          subtitle="Active accounts"
        />
        <StatCard
          title="Active Sessions"
          value={employeesList.filter(e => e.status === 'active').length}
          icon={Activity}
          iconBgColor="bg-[#235DFF]/10"
          iconColor="text-[#235DFF]"
          subtitle="Currently logged in"
        />
        <StatCard
          title="Suspended Accounts"
          value={employeesList.filter(e => e.status === 'suspended').length}
          icon={AlertTriangle}
          iconBgColor="bg-[#03045E]/10"
          iconColor="text-[#03045E]"
          subtitle="Requires attention"
        />
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Input
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
          <Select
            options={[
              { value: '', label: 'All Roles' },
              ...systemRoles.map(r => ({ value: r.id, label: r.name }))
            ]}
            value={selectedRole}
            onChange={setSelectedRole}
            placeholder="Filter by role"
          />
          <Button variant="outline" icon={Filter}>Advanced Filters</Button>
        </div>

        {/* Employee Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Employee</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Department</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Region</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employeesList.map((employee) => (
                <tr key={employee.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div>
                      <p className="font-semibold text-gray-900">{employee.name}</p>
                      <p className="text-sm text-gray-500">{employee.email}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {employee.role}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-gray-700">{employee.department}</td>
                  <td className="py-4 px-4 text-gray-700">{employee.region}</td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      employee.status === 'active' ? 'bg-[#235DFF]/10 text-[#235DFF]' :
                      employee.status === 'suspended' ? 'bg-[#03045E]/10 text-[#03045E]' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {employee.status}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          setSelectedEmployee(employee);
                          setShowEditEmployeeModal(true);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button 
                        onClick={() => handleRevokeEmployee(employee)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <XCircle className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Emergency Revocation */}
      <div className="bg-[#0077B6]/5 border-2 border-[#0077B6]/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#0077B6] rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-red-900 mb-2">Emergency Access Revocation</h3>
            <p className="text-sm text-red-700 mb-4">
              In case of security breach suspicion, immediately disable any employee account using the revocation button.
              All active sessions will be terminated and the action will be logged in the audit trail.
            </p>
            <p className="text-xs text-red-600">
              ⚠️ This action requires System Administrator privileges
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPermissionsTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Permissions & Access Control</h2>
        <p className="text-gray-600 mt-1">Granular permissions for each role</p>
      </div>

      {/* Permissions Matrix */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-6">Access Permissions by Role</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Screen/Module</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Automation Manager</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">FWA Auditor</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Claims Processor</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Actuarial Analyst</th>
              </tr>
            </thead>
            <tbody>
              {permissionsMatrix.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 font-medium text-gray-900">{row.screen}</td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium 
                      ${row.automationManager.color === 'green' ? 'bg-[#235DFF]/10 text-[#235DFF]' :
                        row.automationManager.color === 'blue' ? 'bg-[#0077B6]/10 text-[#0077B6]' :
                        row.automationManager.color === 'yellow' ? 'bg-[#5C7A99]/10 text-[#5C7A99]' :
                        'bg-[#03045E]/10 text-[#03045E]'
                      }`}>
                      {row.automationManager.level}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium 
                      ${row.fwaAuditor.color === 'green' ? 'bg-[#235DFF]/10 text-[#235DFF]' :
                        row.fwaAuditor.color === 'blue' ? 'bg-[#0077B6]/10 text-[#0077B6]' :
                        row.fwaAuditor.color === 'yellow' ? 'bg-[#5C7A99]/10 text-[#5C7A99]' :
                        'bg-[#03045E]/10 text-[#03045E]'
                      }`}>
                      {row.fwaAuditor.level}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium 
                      ${row.claimsProcessor.color === 'green' ? 'bg-[#235DFF]/10 text-[#235DFF]' :
                        row.claimsProcessor.color === 'blue' ? 'bg-[#0077B6]/10 text-[#0077B6]' :
                        row.claimsProcessor.color === 'yellow' ? 'bg-[#5C7A99]/10 text-[#5C7A99]' :
                        'bg-[#03045E]/10 text-[#03045E]'
                      }`}>
                      {row.claimsProcessor.level}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium 
                      ${row.actuarialAnalyst.color === 'green' ? 'bg-[#235DFF]/10 text-[#235DFF]' :
                        row.actuarialAnalyst.color === 'blue' ? 'bg-[#0077B6]/10 text-[#0077B6]' :
                        row.actuarialAnalyst.color === 'yellow' ? 'bg-[#5C7A99]/10 text-[#5C7A99]' :
                        'bg-[#03045E]/10 text-[#03045E]'
                      }`}>
                      {row.actuarialAnalyst.level}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Critical Permissions */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Lock className="w-5 h-5 text-red-600" />
          Critical Granular Permissions
        </h3>
        <div className="space-y-4">
          {criticalPermissions.map((perm, idx) => (
            <div key={idx} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-1">{perm.permission}</h4>
                  <p className="text-sm text-gray-600 mb-2">{perm.purpose}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-700">
                      <strong>Authorized Role:</strong> {perm.authorizedRole}
                    </span>
                    {perm.requiresApproval && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                        Requires Approval
                      </span>
                    )}
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  icon={Settings}
                  onClick={() => {
                    setSelectedPermissionForConfig(perm);
                    setShowPermissionConfigModal(true);
                  }}
                >
                  Configure
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dual Approval Settings */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl border border-orange-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-orange-600" />
          Dual Approval Settings
        </h3>
        <p className="text-gray-700 mb-4">
          Configure actions that require approval from multiple employees before execution.
          Example: STP rule changes require approval from both Automation Manager and Risk Manager.
        </p>
        <Button icon={Settings} onClick={() => setShowApprovalWorkflowModal(true)}>Configure Approval Workflows</Button>
      </div>
    </div>
  );

  const renderAuditTab = () => {
    const filteredLogs = getFilteredAuditLogs();

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Audit Log & Supervision</h2>
          <p className="text-gray-600 mt-1">Track all system actions and changes</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Filter Audit Logs</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input 
              placeholder="Search actions..." 
              icon={Search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select
              options={[
                { value: '', label: 'All Users' },
                { value: 'Maram Al-Sayed', label: 'Maram Al-Sayed' },
                { value: 'Ahmed Al-Mansouri', label: 'Ahmed Al-Mansouri' },
                { value: 'Admin', label: 'Admin' }
              ]}
              placeholder="Filter by user"
              value={selectedUser}
              onChange={(value) => setSelectedUser(value)}
            />
            <Input 
              type="date" 
              placeholder="From date" 
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <Input 
              type="date" 
              placeholder="To date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          {(searchTerm || selectedUser || fromDate || toDate) && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-gray-600">
                Showing {filteredLogs.length} of {auditLogs.length} entries
              </span>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedUser('');
                  setFromDate('');
                  setToDate('');
                }}
                className="text-sm text-[#0077B6] hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Audit Log Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6">
            Recent Activity ({filteredLogs.length} entries)
          </h3>
          <div className="space-y-4">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No audit logs found matching your filters</p>
              </div>
            ) : (
              filteredLogs.map((log) => (
                <div key={log.id} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-[#0077B6]" />
                        <span className="font-bold text-gray-900">{log.action}</span>
                        <span className="text-sm text-gray-500">by {log.user}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{log.details}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {dayjs(log.timestamp).format('MMM DD, YYYY HH:mm')}
                        </span>
                        {log.approver && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700">
                            ✓ Approved by {log.approver}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      icon={Eye}
                      onClick={() => handleViewAuditDetails(log)}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Export Options */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Export Audit Logs</h3>
          <p className="text-sm text-gray-600 mb-4">
            Export filtered audit logs ({filteredLogs.length} entries) in your preferred format
          </p>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              icon={FileText}
              onClick={handleExportAuditPDF}
            >
              Export as PDF
            </Button>
            <Button 
              variant="outline" 
              icon={Database}
              onClick={handleExportAuditCSV}
            >
              Export as CSV
            </Button>
            <Button 
              variant="outline" 
              icon={FileText}
              onClick={handleGenerateReport}
            >
              Generate Report
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">User Management & Permissions</h1>
          <p className="text-lg text-gray-600">
            {dayjs().format('dddd, MMMM D, YYYY')} • Manage roles, employees, and system access
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 px-2">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'roles'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                System Roles
              </div>
            </button>
            <button
              onClick={() => setActiveTab('employees')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'employees'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Employees
              </div>
            </button>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'permissions'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Permissions
              </div>
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'audit'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Audit Log
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'roles' && renderRolesTab()}
          {activeTab === 'employees' && renderEmployeesTab()}
          {activeTab === 'permissions' && renderPermissionsTab()}
          {activeTab === 'audit' && renderAuditTab()}
        </div>
        </div>
      </div>

      {/* Audit Details Modal */}
      {showAuditDetailsModal && selectedAuditLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0077B6]/10 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#0077B6]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Audit Log Details</h3>
                  <p className="text-sm text-gray-600">Action ID: {selectedAuditLog.id}</p>
                </div>
              </div>
              <button
                onClick={() => setShowAuditDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Action Summary */}
            <div className="bg-[#0077B6]/5 rounded-xl p-6 mb-6 border-2 border-[#0077B6]/20">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Action Summary</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Action Type</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedAuditLog.action}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Performed By</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedAuditLog.user}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Timestamp</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {dayjs(selectedAuditLog.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                  </p>
                </div>
                {selectedAuditLog.approver && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Approved By</p>
                    <p className="text-lg font-semibold text-green-700 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      {selectedAuditLog.approver}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Detailed Information */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#0077B6]" />
                Detailed Information
              </h4>
              <p className="text-gray-700 leading-relaxed">{selectedAuditLog.details}</p>
            </div>

            {/* System Information */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">System Information</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Action Category:</span>
                  <span className="font-semibold text-gray-900">
                    {selectedAuditLog.action.includes('Freeze') ? 'Payment Control' :
                     selectedAuditLog.action.includes('Modified') || selectedAuditLog.action.includes('Updated') ? 'Configuration Change' :
                     selectedAuditLog.action.includes('Created') || selectedAuditLog.action.includes('Suspended') ? 'Account Management' :
                     selectedAuditLog.action.includes('Export') ? 'Data Export' :
                     'Investigation'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Requires Approval:</span>
                  <span className="font-semibold text-gray-900">
                    {selectedAuditLog.approver ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Logged At:</span>
                  <span className="font-semibold text-gray-900">
                    {dayjs(selectedAuditLog.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time Since Action:</span>
                  <span className="font-semibold text-gray-900">
                    {dayjs(selectedAuditLog.timestamp).fromNow()}
                  </span>
                </div>
              </div>
            </div>

            {/* Compliance Note */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-yellow-900 mb-1">Compliance & Security</h5>
                  <p className="text-sm text-yellow-800">
                    This action has been recorded in accordance with HIRSTO security policies. 
                    All audit logs are immutable and retained for regulatory compliance purposes.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowAuditDetailsModal(false)} 
                className="flex-1"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  toast.success(`Exporting audit log ${selectedAuditLog.id} as PDF`);
                  setTimeout(() => {
                    toast.success('Audit log entry downloaded successfully');
                  }, 1000);
                }}
                icon={FileText}
                className="flex-1 bg-[#0077B6] hover:bg-[#03045E]"
              >
                Export This Entry
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddEmployeeModal && (
        <AddEmployeeModal
          systemRoles={[...systemRoles, ...customRoles]}
          onClose={() => setShowAddEmployeeModal(false)}
          onSubmit={handleAddEmployee}
        />
      )}

      {/* Edit Employee Modal */}
      {showEditEmployeeModal && selectedEmployee && (
        <EditEmployeeModal
          employee={selectedEmployee}
          systemRoles={[...systemRoles, ...customRoles]}
          onClose={() => {
            setShowEditEmployeeModal(false);
            setSelectedEmployee(null);
          }}
          onSubmit={handleEditEmployee}
        />
      )}

      {/* Create Custom Role Modal */}
      {showCreateRoleModal && (
        <CreateRoleModal
          onClose={() => setShowCreateRoleModal(false)}
          onSubmit={handleCreateCustomRole}
        />
      )}

      {/* View Permissions Modal */}
      {showPermissionsModal && selectedRoleForView && (
        <ViewPermissionsModal
          role={selectedRoleForView}
          onClose={() => {
            setShowPermissionsModal(false);
            setSelectedRoleForView(null);
          }}
        />
      )}

      {/* Approval Workflow Configuration Modal */}
      {showApprovalWorkflowModal && (
        <ApprovalWorkflowModal
          onClose={() => setShowApprovalWorkflowModal(false)}
        />
      )}

      {/* Permission Configuration Modal */}
      {showPermissionConfigModal && selectedPermissionForConfig && (
        <PermissionConfigModal
          permission={selectedPermissionForConfig}
          onClose={() => {
            setShowPermissionConfigModal(false);
            setSelectedPermissionForConfig(null);
          }}
        />
      )}
    </DashboardLayout>
  );
};

// Add Employee Modal Component
const AddEmployeeModal: React.FC<{
  systemRoles: Role[];
  onClose: () => void;
  onSubmit: (employee: any) => void;
}> = ({ systemRoles, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    department: '',
    region: 'All Regions'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Employee</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
            <Input
              placeholder="Enter full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <Input
              type="email"
              placeholder="employee@hirsto.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
            <Input
              type="password"
              placeholder="Create strong password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
            <Select
              options={systemRoles.map(r => ({ value: r.name, label: r.name }))}
              value={formData.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              placeholder="Select role"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
            <Select
              options={[
                { value: 'Claims', label: 'Claims' },
                { value: 'FWA', label: 'FWA' },
                { value: 'Actuarial', label: 'Actuarial' },
                { value: 'Administration', label: 'Administration' }
              ]}
              value={formData.department}
              onChange={(value) => setFormData({ ...formData, department: value })}
              placeholder="Select department"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Geographic Region</label>
            <Select
              options={[
                { value: 'All Regions', label: 'All Regions' },
                { value: 'Dubai', label: 'Dubai' },
                { value: 'Abu Dhabi', label: 'Abu Dhabi' },
                { value: 'Sharjah', label: 'Sharjah' }
              ]}
              value={formData.region}
              onChange={(value) => setFormData({ ...formData, region: value })}
              placeholder="Select region"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1">
              Add Employee
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Employee Modal Component
const EditEmployeeModal: React.FC<{
  employee: Employee;
  systemRoles: Role[];
  onClose: () => void;
  onSubmit: (employee: Employee) => void;
}> = ({ employee, systemRoles, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(employee);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Employee</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
            <Input
              placeholder="Enter full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <Input
              type="email"
              placeholder="employee@hirsto.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
            <Select
              options={systemRoles.map(r => ({ value: r.name, label: r.name }))}
              value={formData.role}
              onChange={(value) => setFormData({ ...formData, role: value })}
              placeholder="Select role"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
            <Select
              options={[
                { value: 'Claims', label: 'Claims' },
                { value: 'FWA', label: 'FWA' },
                { value: 'Actuarial', label: 'Actuarial' },
                { value: 'Administration', label: 'Administration' }
              ]}
              value={formData.department}
              onChange={(value) => setFormData({ ...formData, department: value })}
              placeholder="Select department"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Geographic Region</label>
            <Select
              options={[
                { value: 'All Regions', label: 'All Regions' },
                { value: 'Dubai', label: 'Dubai' },
                { value: 'Abu Dhabi', label: 'Abu Dhabi' },
                { value: 'Sharjah', label: 'Sharjah' }
              ]}
              value={formData.region}
              onChange={(value) => setFormData({ ...formData, region: value })}
              placeholder="Select region"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Custom Role Modal Component
const CreateRoleModal: React.FC<{
  onClose: () => void;
  onSubmit: (role: Omit<Role, 'id'>) => void;
}> = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#8338EC',
    permissions: [] as string[]
  });

  const availablePermissions = [
    { value: 'modify_stp_rules', label: 'Modify STP Rules' },
    { value: 'view_efficiency', label: 'View Efficiency' },
    { value: 'redirect_tasks', label: 'Redirect Tasks' },
    { value: 'freeze_payments', label: 'Freeze Payments' },
    { value: 'start_investigation', label: 'Start Investigation' },
    { value: 'modify_alerts', label: 'Modify Alerts' },
    { value: 'modify_pricing', label: 'Modify Pricing' },
    { value: 'export_data', label: 'Export Data' },
    { value: 'view_analytics', label: 'View Analytics' },
    { value: 'process_claims', label: 'Process Claims' },
    { value: 'view_alerts', label: 'View Alerts' },
    { value: 'view_operational_efficiency', label: 'View Operational Efficiency' },
    { value: 'view_fraud_detection', label: 'View Fraud Detection' },
    { value: 'view_proactive_risk', label: 'View Proactive Risk' }
  ];

  const togglePermission = (permission: string) => {
    if (formData.permissions.includes(permission)) {
      setFormData({
        ...formData,
        permissions: formData.permissions.filter(p => p !== permission)
      });
    } else {
      setFormData({
        ...formData,
        permissions: [...formData.permissions, permission]
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Create Custom Role</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Role Name</label>
            <Input
              placeholder="e.g., Network and Pharmacy Manager"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
            <textarea
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
              rows={3}
              placeholder="Describe the role and its responsibilities"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Color Theme</label>
            <Select
              options={[
                { value: '#03045E', label: 'Dark Blue (#03045E)' },
                { value: '#0077B6', label: 'Bright Blue (#0077B6)' },
                { value: '#235DFF', label: 'Electric Blue (#235DFF)' },
                { value: '#5C7A99', label: 'Steel Blue (#5C7A99)' },
                { value: '#8338EC', label: 'Purple (#8338EC)' },
                { value: '#FB5607', label: 'Orange (#FB5607)' },
                { value: '#06FFA5', label: 'Mint Green (#06FFA5)' }
              ]}
              value={formData.color}
              onChange={(value) => setFormData({ ...formData, color: value })}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Permissions</label>
            <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto p-4 bg-gray-50 rounded-xl">
              {availablePermissions.map((permission) => (
                <label
                  key={permission.value}
                  className="flex items-center gap-2 p-3 bg-white rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(permission.value)}
                    onChange={() => togglePermission(permission.value)}
                    className="w-4 h-4 text-[#0077B6] rounded focus:ring-[#0077B6]"
                  />
                  <span className="text-sm text-gray-700">{permission.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={formData.permissions.length === 0}>
              Create Role
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Permissions Modal Component
const ViewPermissionsModal: React.FC<{
  role: Role;
  onClose: () => void;
}> = ({ role, onClose }) => {
  const permissionLabels: Record<string, string> = {
    'all': 'Full System Access (All Permissions)',
    'modify_stp_rules': 'Modify STP Rules',
    'view_efficiency': 'View Efficiency Metrics',
    'redirect_tasks': 'Redirect Tasks Between Employees',
    'freeze_payments': 'Freeze Provider Payments',
    'start_investigation': 'Start Fraud Investigations',
    'modify_alerts': 'Modify Alert Status',
    'modify_pricing': 'Modify Dynamic Pricing',
    'export_data': 'Export System Data',
    'view_analytics': 'View Analytics Dashboard',
    'process_claims': 'Process Claims',
    'view_alerts': 'View Alerts (Read-Only)',
    'view_operational_efficiency': 'View Operational Efficiency Dashboard',
    'view_fraud_detection': 'View Fraud Detection Dashboard',
    'view_proactive_risk': 'View Proactive Risk Analytics'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${role.color}15` }}
            >
              <Shield className="w-8 h-8" style={{ color: role.color }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{role.name}</h2>
              <p className="text-sm text-gray-600">{role.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="bg-[#0077B6]/5 rounded-xl p-6 mb-6 border-2 border-[#0077B6]/20">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Assigned Permissions</h3>
          <div className="space-y-3">
            {role.permissions.map((permission) => (
              <div 
                key={permission}
                className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200"
              >
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900">
                    {permissionLabels[permission] || permission}
                  </p>
                  {permission === 'all' && (
                    <p className="text-sm text-gray-600 mt-1">
                      This role has unrestricted access to all system features and settings
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-900">Permission Note</p>
              <p className="text-sm text-yellow-700 mt-1">
                These permissions control access to dashboard pages, data operations, and critical actions.
                Changes to role permissions will affect all users assigned to this role.
              </p>
            </div>
          </div>
        </div>

        <Button onClick={onClose} className="w-full">
          Close
        </Button>
      </div>
    </div>
  );
};

// Approval Workflow Configuration Modal Component
const ApprovalWorkflowModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const [workflows, setWorkflows] = useState([
    {
      id: 1,
      action: 'Change STP Rules',
      requiresApproval: true,
      approvers: ['Automation Manager', 'Risk Manager'],
      enabled: true
    },
    {
      id: 2,
      action: 'Freeze Provider Payment',
      requiresApproval: true,
      approvers: ['FWA Auditor', 'Admin'],
      enabled: true
    },
    {
      id: 3,
      action: 'Export Raw Data',
      requiresApproval: true,
      approvers: ['System Administrator', 'Compliance Officer'],
      enabled: true
    },
    {
      id: 4,
      action: 'Modify Pricing Models',
      requiresApproval: true,
      approvers: ['Actuarial Analyst', 'Risk Manager'],
      enabled: false
    }
  ]);

  const toggleWorkflow = (id: number) => {
    setWorkflows(workflows.map(w => 
      w.id === id ? { ...w, enabled: !w.enabled } : w
    ));
  };

  const handleSave = () => {
    toast.success('Approval workflow settings saved successfully');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Configure Approval Workflows</h2>
            <p className="text-gray-600 mt-1">Define actions that require dual approval before execution</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="bg-[#235DFF]/5 rounded-xl p-6 mb-6 border-2 border-[#235DFF]/20">
          <div className="flex items-start gap-3">
            <Settings className="w-5 h-5 text-[#235DFF] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">Dual Approval System</p>
              <p className="text-sm text-gray-600 mt-1">
                Enable dual approval for sensitive actions to ensure accountability and prevent unauthorized changes.
                When enabled, the action requires approval from two different roles before execution.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          {workflows.map((workflow) => (
            <div 
              key={workflow.id}
              className="border border-gray-200 rounded-xl p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{workflow.action}</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={workflow.enabled}
                        onChange={() => toggleWorkflow(workflow.id)}
                        className="w-5 h-5 text-[#0077B6] rounded focus:ring-[#0077B6]"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {workflow.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                  
                  {workflow.enabled && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 mb-2">Requires approval from:</p>
                      <div className="flex flex-wrap gap-2">
                        {workflow.approvers.map((approver, idx) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0077B6]/10 text-[#0077B6]"
                          >
                            {approver}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-orange-900">Important</p>
              <p className="text-sm text-orange-700 mt-1">
                Changes to approval workflows will take effect immediately. Ensure that the designated
                approvers are available and aware of their responsibilities.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1">
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

// Permission Configuration Modal Component
const PermissionConfigModal: React.FC<{
  permission: any;
  onClose: () => void;
}> = ({ permission, onClose }) => {
  const [requiresApproval, setRequiresApproval] = useState(permission.requiresApproval);
  const [authorizedRoles, setAuthorizedRoles] = useState<string[]>([permission.authorizedRole]);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [auditLevel, setAuditLevel] = useState<'standard' | 'detailed' | 'comprehensive'>('detailed');
  const [autoExpiry, setAutoExpiry] = useState(false);
  const [expiryDays, setExpiryDays] = useState(90);

  const handleSave = () => {
    toast.success(`Configuration saved for "${permission.permission}"`);
    onClose();
  };

  const availableRoles = [
    'Administrator',
    'Automation Manager',
    'FWA Fraud Auditor',
    'Actuarial Analyst',
    'Claims Processor',
    'Risk Manager',
    'Compliance Officer',
    'Network Manager'
  ];

  const handleRoleToggle = (role: string) => {
    setAuthorizedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#0077B6]/10 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-[#0077B6]" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Configure Permission</h3>
              <p className="text-sm text-gray-600">{permission.permission}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Permission Details */}
        <div className="bg-[#0077B6]/5 rounded-xl p-4 mb-6 border border-[#0077B6]/20">
          <h4 className="font-semibold text-gray-900 mb-2">Permission Purpose</h4>
          <p className="text-sm text-gray-700">{permission.purpose}</p>
        </div>

        {/* Configuration Options */}
        <div className="space-y-6">
          {/* Approval Requirement */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#0077B6]" />
                  Dual Approval Requirement
                </h4>
                <p className="text-sm text-gray-600">
                  When enabled, this action requires approval from a second authorized person before execution
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiresApproval}
                  onChange={(e) => setRequiresApproval(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#0077B6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0077B6]"></div>
              </label>
            </div>
            {requiresApproval && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  ✓ Approval workflow will be triggered for this permission
                </p>
              </div>
            )}
          </div>

          {/* Authorized Roles */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0077B6]" />
              Authorized Roles
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Select which roles are authorized to perform this action
            </p>
            <div className="grid grid-cols-2 gap-3">
              {availableRoles.map((role) => (
                <label
                  key={role}
                  className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    authorizedRoles.includes(role)
                      ? 'border-[#0077B6] bg-[#0077B6]/5'
                      : 'border-gray-200 hover:border-[#0077B6]/30'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={authorizedRoles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                    className="text-[#0077B6] focus:ring-[#0077B6] rounded"
                  />
                  <span className="ml-2 text-sm text-gray-900">{role}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Audit Level */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0077B6]" />
              Audit Logging Level
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Set the detail level for audit logs when this permission is used
            </p>
            <div className="space-y-2">
              {[
                { value: 'standard', label: 'Standard', desc: 'Basic action logging (who, what, when)' },
                { value: 'detailed', label: 'Detailed', desc: 'Include before/after values and context' },
                { value: 'comprehensive', label: 'Comprehensive', desc: 'Full system state capture with screenshots' }
              ].map((level) => (
                <label
                  key={level.value}
                  className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    auditLevel === level.value
                      ? 'border-[#0077B6] bg-[#0077B6]/5'
                      : 'border-gray-200 hover:border-[#0077B6]/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="auditLevel"
                    value={level.value}
                    checked={auditLevel === level.value}
                    onChange={(e) => setAuditLevel(e.target.value as any)}
                    className="mt-1 text-[#0077B6] focus:ring-[#0077B6]"
                  />
                  <div className="ml-3">
                    <span className="font-semibold text-gray-900">{level.label}</span>
                    <p className="text-xs text-gray-600 mt-1">{level.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#0077B6]" />
                  Real-time Notifications
                </h4>
                <p className="text-sm text-gray-600">
                  Send instant alerts to administrators when this permission is used
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationEnabled}
                  onChange={(e) => setNotificationEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#0077B6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0077B6]"></div>
              </label>
            </div>
          </div>

          {/* Auto-Expiry */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0077B6]" />
                  Permission Auto-Expiry
                </h4>
                <p className="text-sm text-gray-600">
                  Automatically revoke this permission after a specified period (recommended for high-risk actions)
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoExpiry}
                  onChange={(e) => setAutoExpiry(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#0077B6]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0077B6]"></div>
              </label>
            </div>
            {autoExpiry && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Period (days)
                </label>
                <input
                  type="number"
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(parseInt(e.target.value))}
                  min="1"
                  max="365"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Permission will be automatically revoked after {expiryDays} days
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-6">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">Security Notice</p>
              <p className="text-sm text-red-700 mt-1">
                Changes to critical permissions will be logged in the audit trail and may trigger
                security alerts. Ensure compliance with your organization's security policies.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1 bg-[#0077B6] hover:bg-[#005a8a]">
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserManagementDashboard;
